import React, { useState } from 'react';
import { Plus, Trash2, Variable } from 'lucide-react';

/** Chaves de sistema/contato: têm precedência na renderização, então declarar
 *  um parâmetro com o mesmo nome deixaria o campo inerte. O backend recusa. */
const RESERVADAS = [
  'nome', 'primeiro_nome', 'telefone', 'data', 'hora',
  'data_hora', 'dia_semana', 'usuario_nome', 'cliente_nome',
];

/**
 * Declaração dos parâmetros do fluxo — só a forma, não os valores.
 *
 * Os valores ficam por canal (em Configurações › Canais), senão cada uso
 * continuaria exigindo uma cópia do fluxo, que é o problema que o cliente
 * reclamou.
 */
export default function PainelVariaveis({ variaveis = [], onChange, darkMode }) {
  const [erro, setErro] = useState('');

  /** Mensagem do problema da chave, ou '' se estiver ok. */
  const problemaDaChave = (chave, lista, i) => {
    if (!chave) return '';
    if (RESERVADAS.includes(chave)) {
      return `"${chave}" é uma variável de sistema — escolha outro nome.`;
    }
    if (!/^\w+$/.test(chave)) {
      return 'Use apenas letras, números e _ no nome da variável.';
    }
    // Compara contra as OUTRAS linhas: comparar contra a lista inteira antes de
    // gravar a chave nova deixava passar duplicata.
    if (lista.some((v, idx) => idx !== i && String(v.chave || '').toLowerCase() === chave)) {
      return `"${chave}" está declarada mais de uma vez.`;
    }
    return '';
  };

  const atualizar = (i, campo, valor) => {
    if (campo !== 'chave') {
      onChange(variaveis.map((v, idx) => (idx === i ? { ...v, [campo]: valor } : v)));
      return;
    }
    const chave = String(valor || '').trim().toLowerCase();
    const problema = problemaDaChave(chave, variaveis, i);
    setErro(problema);
    // Chave recusada nao entra no estado: antes o aviso aparecia e a chave era
    // gravada de qualquer forma, e fechar o modal apagava o aviso, nao a chave.
    if (problema) return;
    onChange(variaveis.map((v, idx) => (idx === i ? { ...v, chave } : v)));
  };

  const cls = darkMode
    ? 'bg-zinc-700 border-zinc-600 text-gray-200 placeholder-gray-500'
    : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400';

  return (
    <div className={`mt-1 p-3 rounded-lg border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Variable className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Parâmetros do fluxo
          </span>
        </div>
        <button
          type="button"
          onClick={() => onChange([...variaveis, { chave: '', label: '', obrigatoria: false, padrao: '' }])}
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
            darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <Plus className="h-3 w-3" /> Adicionar
        </button>
      </div>

      {variaveis.length === 0 ? (
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Declare um parâmetro para reaproveitar o mesmo fluxo em vários canais —
          ex.: <code>unidade</code>, <code>horario</code>, <code>link_cardapio</code>.
          O valor de cada canal é preenchido em Configurações › Canais.
        </p>
      ) : (
        <div className="space-y-2">
          {variaveis.map((v, i) => (
            <div key={`var-${i}`} className="flex flex-wrap gap-2 items-center">
              <input
                value={v.chave || ''}
                onChange={(e) => atualizar(i, 'chave', e.target.value)}
                placeholder="chave"
                className={`text-xs font-mono border rounded px-2 py-1.5 w-28 ${cls}`}
              />
              <input
                value={v.label || ''}
                onChange={(e) => atualizar(i, 'label', e.target.value)}
                placeholder="Rótulo (ex.: Nome da unidade)"
                className={`text-xs border rounded px-2 py-1.5 flex-1 min-w-[140px] ${cls}`}
              />
              <input
                value={v.padrao || ''}
                onChange={(e) => atualizar(i, 'padrao', e.target.value)}
                placeholder="Valor padrão"
                className={`text-xs border rounded px-2 py-1.5 w-32 ${cls}`}
              />
              <label className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <input
                  type="checkbox"
                  checked={!!v.obrigatoria}
                  onChange={(e) => atualizar(i, 'obrigatoria', e.target.checked)}
                />
                Obrigatória
              </label>
              <button
                type="button"
                onClick={() => onChange(variaveis.filter((_, idx) => idx !== i))}
                className="p-1 rounded text-red-500 hover:bg-red-500/10"
                title="Remover"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              {/* Sem valor padrão E sem valor por canal, esta variável é APAGADA
                  no envio (vira vazio). É o que fez o cliente achar que "sumiu".
                  Obrigatória tem tratamento próprio (bloqueia a ativação), então
                  o aviso é só para a opcional sem default. */}
              {v.chave && !v.padrao && !v.obrigatoria && (
                <span className="w-full text-[11px] text-amber-500">
                  ⚠️ Sem valor padrão: se o canal não preencher <code>{`{{${v.chave}}}`}</code>, ela sai vazia na mensagem.
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {erro && <p className="text-xs text-red-500 mt-2">{erro}</p>}
    </div>
  );
}
